import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Row,
  Col,
  ListGroup,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Image,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaCalendarAlt,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaUserAlt,
  FaPhoneAlt,
  FaGlobe,
  FaTools,
  FaBoxOpen,
  FaInfo,
  FaTimesCircle,
  FaExclamationTriangle,
  FaPlay,
  FaImage,
  FaVideo,
  FaRuler,
  FaClipboardList,
  FaExpand,
  FaClock,
  FaTasks,
  FaHistory,
  FaArrowDown,
  FaStar,
  FaMicrophone,
  FaFileInvoiceDollar,
  FaChevronDown,
  FaChevronRight,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getJob, updateJobStatus, cancelJob } from "../../services/jobService";
import "./JobDetails.css";
import MobileJobWorker from "./MobileJobWorker";
import AddItemsToJobModal from "./AddItemsToJobModal";
import api from "../../services/api";
import { formatJobNumber } from "../../utils/jobUtils";

/**
 * Environment-aware logging helpers
 * Only log in development to reduce production console noise
 */
const isDev = process.env.NODE_ENV === 'development';
const log = () => {};
const warn = (...args) => isDev && console.warn(...args);
const error = (...args) => console.error(...args); // Always log errors

/**
 * Enhanced JobDetails Component with Time Tracking and Actions Review
 *
 * Displays comprehensive job information with door details, time tracking,
 * actions performed, and media review for management oversight.
 */
const JobDetails = () => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [error, setError] = useState(null);
  const [doorMedia, setDoorMedia] = useState({});
  const [loadingMedia, setLoadingMedia] = useState({});
  const [expandedDoors, setExpandedDoors] = useState(new Set());
  const [doorTimeTracking, setDoorTimeTracking] = useState({});
  const [doorActions, setDoorActions] = useState({});
  const [cancellationReason, setCancellationReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [showMobileWorker, setShowMobileWorker] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [purchaseOrdersExpanded, setPurchaseOrdersExpanded] = useState(false);
  const { jobId } = useParams();
  const navigate = useNavigate();

  /**
   * Load job details from API
   */
  const loadJob = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getJob(jobId);
      if (data && data.scheduled_date) {
        log("Raw scheduled date from API:", data.scheduled_date);
      }
      setJob(data);
      // Load media and time tracking for each door
      if (data && data.doors) {
        loadDoorMedia(data.doors);
      }
    } catch (err) {
      error("Error loading job:", err);
      setError("Failed to load job details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  /**
   * Load purchase orders for this job
   */
  const loadPurchaseOrders = useCallback(async () => {
    setLoadingPurchaseOrders(true);
    try {
      const response = await api.get(`/purchase-orders`, {
        params: { job_id: jobId }
      });
      setPurchaseOrders(response.data || []);
    } catch (err) {
      console.error("Error loading purchase orders:", err);
      setPurchaseOrders([]);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  }, [jobId]);

  const loadDoorTimeTrackingAndActions = useCallback(async (doors) => {

    try {
      // Variable to store the total job minutes for later use
      let totalJobMinutes = 0;
      
      // Load time tracking data for the job

      try {
        // Use the api service instead of direct fetch
        const timeResponse = await api.get(`/mobile/jobs/${jobId}/time-tracking`);

        // Process the time data
        const timeData = timeResponse.data;
        
        // Fix time calculation - properly handle active sessions
        let calculatedTotalMinutes = timeData.total_minutes || 0;
        const sessions = timeData.sessions || [];
        
        // Calculate time for active sessions
        sessions.forEach(session => {
          if (session.status === 'active' && session.start_time) {
            const startTime = new Date(session.start_time);
            const currentTime = new Date();
            const activeMinutes = Math.floor((currentTime - startTime) / (1000 * 60));
            calculatedTotalMinutes += activeMinutes;
            session.current_minutes = activeMinutes;

          }
        });
        
        const processedTimeData = {
          jobId: timeData.job_id || parseInt(jobId),
          totalMinutes: calculatedTotalMinutes,
          totalHours: Math.round((calculatedTotalMinutes / 60) * 100) / 100,
          sessions: sessions,
          jobTimingStatus: timeData.job_timing_status || 'not_started'
        };
        

        setDoorTimeTracking(processedTimeData);
        
        // Store the total minutes for door time distribution
        totalJobMinutes = calculatedTotalMinutes;
        
      } catch (timeError) {
        // Handle error - could be 404 or network error
        if (timeError.response?.status === 404) {
          console.warn('⚠️ Time tracking endpoint not found (404). Using default values.');
        } else {
          console.error('❌ Error fetching time tracking data:', timeError);
        }
        
        const fallbackTimeData = {
          jobId: parseInt(jobId),
          totalMinutes: 0,
          totalHours: 0,
          sessions: [],
          jobTimingStatus: 'not_started'
        };
        setDoorTimeTracking(fallbackTimeData);
        totalJobMinutes = 0;
      }

      // Load actions performed for each door

      const doorActionsData = {};
      
      if (!doors || doors.length === 0) {
        console.warn('⚠️ No doors provided for action loading');
        setDoorActions({});
        return;
      }
      
      for (const door of doors) {
        const doorId = door.id;

        try {
          // Use the api service for door actions
          const actionsResponse = await api.get(`/doors/${doorId}/actions`, {
            params: { job_id: jobId }
          });
          

          const actionsData = actionsResponse.data;
          const actions = actionsData.actions || [];

          // Properly filter and process line item completions
          const lineItemCompletions = actions.filter(action => 
            action.type === 'line_item_completion' && action.completed === true
          );

          // Extract other action types
          const timeEntries = actions.filter(action => action.type === 'time_tracking');
          const mediaUploads = actions.filter(action => action.type === 'media_upload');
          const signatures = actions.filter(action => 
            action.type === 'signature' || action.type === 'job_signature'
          );
          



          // Calculate last activity timestamp
          const allTimestamps = actions
            .map(action => action.timestamp)
            .filter(timestamp => timestamp)
            .map(timestamp => new Date(timestamp).getTime())
            .filter(time => !isNaN(time));
          
          const lastActivity = allTimestamps.length > 0 ? Math.max(...allTimestamps) : null;

          // Create the door actions data with properly formatted line items
          const processedLineItems = lineItemCompletions.map(action => ({
            id: action.line_item_id || action.id,
            description: action.line_item_description || action.description || 'Completed work item',
            completed_at: action.timestamp,
            completed_by_name: action.completed_by_name || action.user_name,
            completed_by: action.completed_by_id || action.user_id,
            part_number: action.part_number,
            quantity: action.quantity || 1,
            completed: true
          }));
          

          // Update door media status based on actual media uploads
          const photos = mediaUploads.filter(action => action.media_type === 'photo');
          const videos = mediaUploads.filter(action => action.media_type === 'video');
          door.has_photo = photos.length > 0;
          door.has_video = videos.length > 0;

          // Update door completion status based on BOTH signature and photo presence
          if (signatures.length > 0 && door.has_photo) {
            door.completed = true;
          } else {
            door.completed = false;
          }
          
          doorActionsData[doorId] = {
            lineItemsCompleted: processedLineItems,
            totalActions: actions.length,
            timeSpent: 0, // Will be calculated later
            lastActivity: lastActivity,
            actions: actions,
            summary: {
              lineItemCompletions: lineItemCompletions.length,
              timeEntries: timeEntries.length,
              mediaUploads: mediaUploads.length,
              signatures: signatures.length,
              totalActions: actions.length
            },
            mediaInfo: {
              photos: photos.length,
              videos: videos.length,
              total: mediaUploads.length
            },
            fallback: false
          };
          
          // Update the door object to include the completed line items
          if (processedLineItems.length > 0) {
            door.line_items = door.line_items || [];
            
            // Merge completed items into door line items
            processedLineItems.forEach(completedItem => {
              const existingItem = door.line_items.find(item => item.id === completedItem.id);
              if (existingItem) {
                Object.assign(existingItem, completedItem);
              } else {
                door.line_items.push(completedItem);
              }
            });
          }
          

        } catch (doorError) {
          // Handle error - could be 404 or other error
          if (doorError.response?.status === 404) {
            console.warn(`⚠️ Door ${doorId} actions endpoint not found (404). Using fallback data.`);
          } else {
            console.error(`❌ Error loading actions for door ${doorId}:`, doorError);
          }
          
          // Fallback: use existing door data
          const completedItems = door.line_items ? door.line_items.filter(item => item.completed) : [];

          doorActionsData[doorId] = {
            lineItemsCompleted: completedItems,
            totalActions: completedItems.length,
            timeSpent: 0,
            lastActivity: null,
            actions: [],
            summary: {
              lineItemCompletions: completedItems.length,
              timeEntries: 0,
              mediaUploads: 0,
              signatures: 0,
              totalActions: completedItems.length
            },
            mediaInfo: {
              photos: 0,
              videos: 0,
              total: 0
            },
            fallback: true,
            error: doorError.message || 'Failed to load actions'
          };
        }
      }
      
      // Calculate time spent per door based on total job time and actions

      if (totalJobMinutes > 0) {
        const totalActions = Object.values(doorActionsData).reduce((sum, door) => sum + door.totalActions, 0);

        if (totalActions > 0) {
          Object.keys(doorActionsData).forEach(doorId => {
            const doorActions = doorActionsData[doorId];
            const doorWeight = doorActions.totalActions / totalActions;
            doorActions.timeSpent = Math.round(totalJobMinutes * doorWeight);

          });
        }
      } else {
        console.warn('🕐 No job time to distribute across doors');
      }
      

      setDoorActions(doorActionsData);
      
      // Update the job state to reflect the modified doors with line items
      setJob(prevJob => ({
        ...prevJob,
        doors: doors
      }));
      
      // Log summary
      const totalActions = Object.values(doorActionsData).reduce((sum, door) => sum + door.totalActions, 0);
      const totalCompletedItems = Object.values(doorActionsData).reduce((sum, door) => sum + door.lineItemsCompleted.length, 0);

    } catch (error) {
      console.error('❌ Fatal error in loadDoorTimeTrackingAndActions:', error);
      setDoorActions({});
      setDoorTimeTracking({
        jobId: parseInt(jobId),
        totalMinutes: 0,
        totalHours: 0,
        sessions: [],
        jobTimingStatus: 'error'
      });
    }
  }, [jobId]);

  // Also update the loadDoorMedia function:
  const loadDoorMedia = useCallback(async (doors) => {
    const mediaPromises = doors.map(async (door) => {
      // Set loading state for this door
      setLoadingMedia((prev) => ({ ...prev, [door.id]: true }));

      try {
        // Use the api service for fetching door media

        const mediaResponse = await api.get(`/mobile/doors/${door.id}/media`, {
          params: { job_id: jobId }
        });

        const mediaData = mediaResponse.data;

        // Process photos - use direct_url if available (Azure Storage), otherwise fetch blob
        const processedPhotos = await Promise.all((mediaData.photos || []).map(async (photo) => {
          // If we have a direct URL (Azure Storage), use it directly
          if (photo.direct_url) {
            return {
              ...photo,
              blobUrl: photo.direct_url
            };
          }

          // Otherwise, try to fetch through the API
          try {
            const mediaResponse = await api.get(`/mobile/media/${photo.id}/photo`, {
              responseType: 'blob',
              validateStatus: (status) => status < 600, // Accept all status codes
              _skipErrorLogging: true // Custom flag to skip error logging
            });

            if (mediaResponse.status >= 400) {
              // Silently skip photos that can't be loaded
              return { ...photo, missing: true };
            }

            const blobUrl = URL.createObjectURL(mediaResponse.data);
            return {
              ...photo,
              blobUrl: blobUrl
            };
          } catch (error) {
            // Silently handle any other errors
            return { ...photo, error: true };
          }
        }));

        // Process videos - use direct_url if available (Azure Storage), otherwise fetch blob
        const processedVideos = await Promise.all((mediaData.videos || []).map(async (video) => {
          // If we have a direct URL (Azure Storage), use it directly
          if (video.direct_url) {
            return {
              ...video,
              blobUrl: video.direct_url
            };
          }

          // Otherwise, try to fetch through the API
          try {
            const mediaResponse = await api.get(`/mobile/media/${video.id}/video`, {
              responseType: 'blob',
              validateStatus: (status) => status < 600, // Accept all status codes
              _skipErrorLogging: true // Custom flag to skip error logging
            });

            if (mediaResponse.status >= 400) {
              // Silently skip videos that can't be loaded
              return { ...video, missing: true };
            }

            const blobUrl = URL.createObjectURL(mediaResponse.data);
            return {
              ...video,
              blobUrl: blobUrl
            };
          } catch (error) {
            // Silently handle any other errors
            return { ...video, error: true };
          }
        }));

        // Update door media state with blob URLs
        setDoorMedia((prev) => ({
          ...prev,
          [door.id]: {
            ...mediaData,
            photos: processedPhotos,
            videos: processedVideos
          },
        }));

        // Update door media status flags based on loaded media
        if (mediaData.photos && mediaData.photos.length > 0) {
          door.has_photo = true;
        }
        if (mediaData.videos && mediaData.videos.length > 0) {
          door.has_video = true;
        }

      } catch (error) {
        // Handle error - could be 404 or network error
        if (error.response?.status === 404) {
          console.warn(`⚠️ Media endpoint for door ${door.id} returned 404. Using empty media array.`);
        } else {
          console.error(`❌ Error loading media for door ${door.id}:`, error);
        }

        // Set empty media data as fallback
        setDoorMedia((prev) => ({
          ...prev,
          [door.id]: {
            photos: [], 
            videos: [],
            _fallback: true,
            _error: error.message || 'Failed to load media'
          },
        }));
      } finally {
        // Always clear loading state for this door
        setLoadingMedia((prev) => ({ ...prev, [door.id]: false }));
      }
    });

    // Wait for all media loading operations to complete
    await Promise.all(mediaPromises);

    // Also load time tracking and actions data
    // This function has its own error handling
    await loadDoorTimeTrackingAndActions(doors);
  }, [jobId, loadDoorTimeTrackingAndActions]);

  /**
   * Toggle door expansion for detailed view
   */
  const toggleDoorExpansion = (doorId) => {
    setExpandedDoors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(doorId)) {
        newSet.delete(doorId);
      } else {
        newSet.add(doorId);
      }
      return newSet;
    });
  };

  /**
   * Load job data on component mount
   */
  useEffect(() => {
    loadJob();
    loadPurchaseOrders();
  }, [loadJob, loadPurchaseOrders]);
  
  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      // Clean up blob URLs to prevent memory leaks
      Object.values(doorMedia).forEach(media => {
        if (media.photos) {
          media.photos.forEach(photo => {
            if (photo.blobUrl) {
              URL.revokeObjectURL(photo.blobUrl);
            }
          });
        }
        if (media.videos) {
          media.videos.forEach(video => {
            if (video.blobUrl) {
              URL.revokeObjectURL(video.blobUrl);
            }
          });
        }
      });
    };
  }, [doorMedia]);

  /**
   * Handle job status change
   */
  const handleStatusChange = async (newStatus) => {
    try {
      await updateJobStatus(job.id, { status: newStatus });
      toast.success(`Job status updated to ${newStatus.replace("_", " ")}`);
      loadJob();
    } catch (error) {
      console.error("Error updating job status:", error);
      toast.error("Error updating job status");
    }
  };

  /**
   * Cancel a job by calling the dedicated cancel endpoint
   */
  const handleCancelJob = async () => {
    try {
      setIsCancelling(true);
      const cancelData = {};
      if (cancellationReason.trim()) {
        cancelData.reason = cancellationReason.trim();
      }
      await cancelJob(job.id, cancelData);
      toast.success("Job has been cancelled successfully");
      setCancellationReason("");
      setShowCancelModal(false);
      loadJob();
    } catch (error) {
      console.error("Error cancelling job:", error);
      toast.error(`Error cancelling job: ${error.message || "Unknown error"}`);
    } finally {
      setIsCancelling(false);
    }
  };

  /**
   * Enhanced formatDuration function with better time display
   */
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "No time recorded";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  /**
   * Calculate estimated time spent on a door based on actions and job time
   */
  const estimateDoorTime = (door) => {
    const doorActionData = doorActions[door.id];
    if (!doorActionData) return 0;
    // Use the calculated time spent from actions data
    return doorActionData.timeSpent || 0;
  };

  /**
   * Download media file (photo or video)
   * @param {number} mediaId - The ID of the media to download
   * @param {string} mediaType - Type of media ('photo' or 'video')
   * @param {string} fileName - Suggested filename for download
   */
  const downloadMedia = async (mediaId, mediaType, fileName) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading(`Downloading ${mediaType}...`);
      
      // Use api service to fetch the media file with proper backend URL
      const response = await api.get(`/mobile/media/${mediaId}/${mediaType}`, {
        responseType: 'blob' // Important: tell axios to expect binary data
      });
      
      // Get the blob data from axios response
      const blob = response.data;
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      
      // Append to body, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      // Update toast to success
      toast.update(loadingToast, {
        render: `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} downloaded successfully!`,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
    } catch (error) {
      console.error(`Error downloading ${mediaType}:`, error);
      toast.error(`Failed to download ${mediaType}. Please try again.`);
    }
  };

  /**
   * Get the full URL for media files using the backend base URL
   */
  const getMediaUrl = (mediaId, mediaType) => {
    // Get the base URL from the api config
    const baseURL = api.defaults.baseURL || 'https://scott-overhead-doors.azurewebsites.net/api';
    return `${baseURL}/mobile/media/${mediaId}/${mediaType}`;
  };

  /**
   * Render door media gallery with download functionality
   */
  const renderDoorMedia = (door) => {
    const media = doorMedia[door.id];
    const isLoading = loadingMedia[door.id];

    if (isLoading) {
      return (
        <div className="door-media-loading">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading media...</span>
        </div>
      );
    }

    if (!media || (media.photos.length === 0 && media.videos.length === 0)) {
      return (
        <div className="door-media-empty">
          <FaImage className="me-2" />
          <span>No photos or videos uploaded yet</span>
        </div>
      );
    }

    return (
      <div className="door-media-gallery">
        {/* Photos */}
        {media.photos.length > 0 && (
          <div className="media-section">
            <h6 className="media-section-title">
              <FaImage className="me-2" />
              Photos ({media.photos.filter(p => !p.missing && !p.error).length})
            </h6>
            <div className="media-grid">
              {media.photos.filter(photo => !photo.missing && !photo.error).map((photo, index) => {
                // Generate filename for download
                const photoFileName = `door_${door.door_number || door.id}_photo_${index + 1}_${new Date(photo.uploaded_at).toISOString().split('T')[0]}.jpg`;
                
                return (
                  <div key={photo.id} className="media-item">
                    <div className="media-container">
                      <Image
                        src={photo.blobUrl || getMediaUrl(photo.id, 'photo')}
                        thumbnail
                        className="door-photo"
                        alt={`Door ${door.door_number} photo ${index + 1}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="media-overlay">
                        <Button
                          size="sm"
                          className="download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadMedia(photo.id, 'photo', photoFileName);
                          }}
                          title="Download photo"
                        >
                          <FaArrowDown />
                        </Button>
                      </div>
                    </div>
                    <div className="media-info">
                      <small className="text-muted">
                        {new Date(photo.uploaded_at).toLocaleDateString()}
                      </small>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="download-btn-alt"
                        onClick={() => downloadMedia(photo.id, 'photo', photoFileName)}
                      >
                        <FaArrowDown className="me-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Videos */}
        {media.videos.length > 0 && (
          <div className="media-section">
            <h6 className="media-section-title">
              <FaVideo className="me-2" />
              Videos ({media.videos.filter(v => !v.missing && !v.error).length})
            </h6>
            <div className="media-grid">
              {media.videos.filter(video => !video.missing && !video.error).map((video, index) => {
                // Generate filename for download
                const videoFileName = `door_${door.door_number || door.id}_video_${index + 1}_${new Date(video.uploaded_at).toISOString().split('T')[0]}.mp4`;
                
                return (
                  <div key={video.id} className="media-item">
                    <div className="media-container">
                      <video controls className="door-video" preload="metadata"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}>
                        <source src={video.blobUrl || getMediaUrl(video.id, 'video')} />
                        Your browser does not support the video tag.
                      </video>
                      <div className="media-overlay">
                        <Button
                          size="sm"
                          className="download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadMedia(video.id, 'video', videoFileName);
                          }}
                          title="Download video"
                        >
                          <FaArrowDown />
                        </Button>
                      </div>
                    </div>
                    <div className="media-info">
                      <small className="text-muted">
                        {new Date(video.uploaded_at).toLocaleDateString()}
                      </small>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="download-btn-alt"
                        onClick={() => downloadMedia(video.id, 'video', videoFileName)}
                      >
                        <FaArrowDown className="me-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDoorTimeAndActions = (door) => {
    const doorActionData = doorActions[door.id];

    // Use safe action data with proper defaults
    const safeActionData = doorActionData || {
      lineItemsCompleted: [], totalActions: 0, timeSpent: 0, lastActivity: null,
      summary: { lineItemCompletions: 0, timeEntries: 0, mediaUploads: 0, signatures: 0, totalActions: 0 },
      fallback: true, error: 'No action data available'
    };

    const estimatedTime = safeActionData.timeSpent || 0;
    
    // --- IMPROVED LOGIC FOR WORK ITEMS ---
    const totalLineItems = door.line_items?.length || 0;
    const completedLineItems = safeActionData.lineItemsCompleted?.length || 0;
    const workProgress = totalLineItems > 0 ? Math.round((completedLineItems / totalLineItems) * 100) : (completedLineItems > 0 ? 100 : 0);
    // --- END IMPROVEMENT ---

    const hasPhoto = door.has_photo || (safeActionData.mediaInfo?.photos || 0) > 0;
    const hasVideo = door.has_video || (safeActionData.mediaInfo?.videos || 0) > 0;
    const hasSignature = (safeActionData.summary?.signatures || 0) > 0;
    
    // Helper component for status items
    const StatusItem = ({ icon, label, status, isComplete }) => (
      <div className="progress-item">
        <span className="progress-label">
          {icon} {label}:
        </span>
        <span className={`progress-status ${isComplete ? 'status-complete' : 'status-pending'}`}>
          {status}
        </span>
      </div>
    );

    return (
      <div className="door-time-actions">
        <h6 className="time-actions-title">
          <FaTasks className="me-2" />
          Door Activity Summary
          {safeActionData.fallback && (
            <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
              Fallback Data
            </Badge>
          )}
        </h6>
        
        {/* New "At a Glance" Summary */}
        <div className="at-a-glance-summary">
          <div className="glance-item">
            <div className="glance-value">{formatDuration(estimatedTime)}</div>
            <div className="glance-label"><FaClock className="me-1"/>Time Spent</div>
          </div>
          <div className="glance-item">
            <div className="glance-value">{safeActionData.totalActions}</div>
            <div className="glance-label"><FaHistory className="me-1"/>Total Actions</div>
          </div>
          <div className="glance-item">
            <div className="glance-value">{workProgress}%</div>
            <div className="glance-label"><FaClipboardList className="me-1"/>Work Done</div>
          </div>
        </div>

        {/* Improved Progress Checklist */}
        <div className="progress-checklist">
          <StatusItem 
            icon={<FaClipboardList />} 
            label="Work Items" 
            status={`${completedLineItems} / ${totalLineItems} Completed`}
            isComplete={completedLineItems >= totalLineItems && totalLineItems > 0} 
          />
          <StatusItem 
            icon={<FaImage />} 
            label="Photo" 
            status={hasPhoto ? "Captured" : "Pending"}
            isComplete={hasPhoto}
          />
          <StatusItem 
            icon={<FaVideo />} 
            label="Video" 
            status={hasVideo ? "Recorded" : "Pending"}
            isComplete={hasVideo}
          />
          <StatusItem 
            icon={<FaCheckCircle />} 
            label="Signature" 
            status={hasSignature ? "Signed Off" : "Pending"}
            isComplete={hasSignature}
          />
        </div>
        
        {safeActionData.lastActivity && (
          <div className="last-activity-log">
            <strong>Last Activity:</strong> {new Date(safeActionData.lastActivity).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  const parseDatePreservingDay = (dateStr) => {
    if (!dateStr) return null;

    try {
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0];
      }
      if (dateStr.includes(",")) {
        const tempDate = new Date(dateStr);
        const year = tempDate.getUTCFullYear();
        const month = tempDate.getUTCMonth();
        const day = tempDate.getUTCDate();
        return new Date(year, month, day, 12, 0, 0);
      }
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day, 12, 0, 0);
      }
      const date = new Date(dateStr);
      date.setHours(12, 0, 0, 0);
      return date;
    } catch (e) {
      console.error("Error parsing date:", e, dateStr);
      return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date Null : Not Scheduled";
    const parsedDate = parseDatePreservingDay(dateString);
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      console.warn("Invalid date:", dateString);
      return "Date Null : Not Scheduled";
    }
    const options = { year: "numeric", month: "short", day: "numeric" };
    return parsedDate.toLocaleDateString(undefined, options);
  };
  
  const renderDoorLineItems = (door) => {

    const doorActionData = doorActions[door.id];
    let lineItems = [];
    
    // Prioritize line items from actions data (more accurate)
    if (doorActionData && doorActionData.lineItemsCompleted && doorActionData.lineItemsCompleted.length > 0) {
      lineItems = doorActionData.lineItemsCompleted;

    } else if (door.line_items && door.line_items.length > 0) {
      lineItems = door.line_items;

    }
    
    if (!lineItems || lineItems.length === 0) {
      return (
        <div className="door-line-items-empty">
          <FaClipboardList className="me-2" />
          <span>No work items completed</span>
          {doorActionData && doorActionData.totalActions > 0 && (
            <div className="text-muted" style={{ fontSize: '0.9em', marginTop: '5px' }}>
              {doorActionData.totalActions} other actions recorded
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="door-line-items">
        <h6 className="line-items-title">
          <FaClipboardList className="me-2" />
          Work Items ({lineItems.length})
        </h6>
        <ListGroup variant="flush" className="line-items-list">
          {lineItems.map((item, index) => (
            <ListGroup.Item key={item.id || index} className="line-item">
              <div className="line-item-content">
                <div className="line-item-header">
                  <span className="line-item-description">
                    {item.description || `Work item ${index + 1}`}
                  </span>
                  {item.completed && (
                    <Badge bg="success" className="ms-2">
                      <FaCheckCircle className="me-1" />
                      Completed
                    </Badge>
                  )}
                </div>
                {item.part_number && (
                  <div className="line-item-detail">
                    <small className="text-muted">Part: {item.part_number}</small>
                  </div>
                )}
                <div className="line-item-details">
                  <small className="text-muted">
                    Qty: {item.quantity || 1}
                    {item.price && ` | Price: $${(item.price || 0).toFixed(2)}`}
                    {item.labor_hours && ` | Labor: ${item.labor_hours}h`}
                  </small>
                </div>
                {item.completed_at && (
                  <div className="line-item-completion">
                    <small className="text-success">
                      <FaClock className="me-1" />
                      Completed: {new Date(item.completed_at).toLocaleString()}
                      {item.completed_by_name && ` by ${item.completed_by_name}`}
                    </small>
                  </div>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    );
  };

  /**
   * Render door specifications
   */
  const renderDoorSpecs = (door) => {
    const specs = [];

    if (door.location) {
      specs.push({
        label: "Location",
        value: door.location,
        icon: FaMapMarkerAlt,
      });
    }

    if (door.door_type) {
      specs.push({ label: "Type", value: door.door_type, icon: FaTools });
    }

    if (door.width && door.height) {
      const dimensions = `${door.width} × ${door.height} ${
        door.dimension_unit || "ft"
      }`;
      specs.push({ label: "Dimensions", value: dimensions, icon: FaRuler });
    }

    if (door.labor_description) {
      specs.push({
        label: "Work Description",
        value: door.labor_description,
        icon: FaClipboardList,
      });
    }

    if (specs.length === 0) {
      return null; // Don't render anything if no specs
    }

    return (
      <div className="door-specifications">
        <h6 className="specs-title">
          <FaInfo className="me-2" />
          Specifications
        </h6>
        <div className="specs-grid">
          {specs.map((spec, index) => (
            <div key={index} className="spec-item">
              <div className="spec-label">
                <spec.icon className="me-2" />
                {spec.label}:
              </div>
              <div className="spec-value">{spec.value}</div>
            </div>
          ))}
        </div>
        {door.notes && (
          <div className="door-notes">
            <strong>Notes:</strong> {door.notes}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Loading job details...</p>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Alert variant="danger" className="error-alert">
        <Alert.Heading>Error Loading Job</Alert.Heading>
        <p>{error}</p>
        <div className="d-flex justify-content-between">
          <Button variant="outline-danger" onClick={() => navigate("/jobs")}>
            Back to Jobs
          </Button>
          <Button variant="primary" onClick={loadJob}>
            Try Again
          </Button>
        </div>
      </Alert>
    );
  }

  /**
   * Render not found state
   */
  if (!job) {
    return (
      <Alert variant="warning" className="not-found-alert">
        <Alert.Heading>Job Not Found</Alert.Heading>
        <p>The requested job could not be found.</p>
        <Button variant="primary" onClick={() => navigate("/jobs")}>
          Back to Jobs
        </Button>
      </Alert>
    );
  }

  // If showMobileWorker is true, render MobileJobWorker instead of JobDetails UI
  if (showMobileWorker) {
    return <MobileJobWorker jobId={job.id.toString()} />;
  }

  const displayDate = job.formatted_date || formatDate(job.scheduled_date);
  const isJobCancelled = job.status === "cancelled";
  const canStartJobMobile =
    job && !isJobCancelled && job.status !== "completed";

  return (
    <div className="job-details-container">
      <div className="job-details-header">
        <div className="job-title">
          <h2>
            Job #{formatJobNumber(job.job_number)}
            {job.is_special && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>
                <FaStar className="me-1" />
                Special
              </Badge>
            )}
          </h2>
          <div className="d-flex align-items-center mb-2">
            <span className="status-label me-3">Status:</span>
            <span
              className={`status-text ${isJobCancelled ? "text-danger" : ""}`}
            >
              {job.status
                ? job.status.charAt(0).toUpperCase() +
                  job.status.slice(1).replace(/_/g, " ")
                : "No Status"}
            </span>
            {job.is_special && job.estimate_id && (
              <Button
                variant="warning"
                size="sm"
                className="d-flex align-items-center gap-1 ms-3"
                onClick={() => setShowAddItemsModal(true)}
              >
                <FaMicrophone />
                Add Items via Voice
              </Button>
            )}
          </div>
        </div>
        <div className="job-actions">
          {canStartJobMobile && (
            <Button
              variant="success"
              className="action-button"
              onClick={() => setShowMobileWorker(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                opacity: 1,
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                backgroundImage: 'none',
                gap: '8px'
              }}
            >
              <FaPlay /> Start Job (Mobile)
            </Button>
          )}
          {!isJobCancelled && (
            <>
              <Button
                variant="outline-primary"
                className="action-button"
                onClick={() => navigate(`/schedule/job/${job.id}`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  opacity: 1,
                  backgroundColor: '#17a2b8',
                  borderColor: '#17a2b8',
                  color: '#ffffff',
                  backgroundImage: 'none'
                }}
              >
                <FaCalendarAlt className="me-2" /> Schedule
              </Button>
              <Button
                variant="outline-danger"
                className="action-button"
                onClick={() => setShowCancelModal(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  opacity: 1,
                  backgroundColor: '#dc3545',
                  borderColor: '#dc3545',
                  color: '#ffffff',
                  backgroundImage: 'none'
                }}
              >
                <FaTimesCircle className="me-2" /> Cancel Job
              </Button>
            </>
          )}
          <Button
            variant="outline-secondary"
            className="action-button"
            onClick={() => {
              if (showMobileWorker) setShowMobileWorker(false);
              else navigate("/jobs");
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              opacity: 1,
              backgroundColor: '#6c757d',
              borderColor: '#6c757d',
              color: '#ffffff',
              backgroundImage: 'none'
            }}
          >
            <FaArrowLeft className="me-2" /> Back to Jobs
          </Button>
        </div>
      </div>

      {isJobCancelled && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>
            <FaExclamationTriangle className="me-2" /> This job has been
            cancelled
          </Alert.Heading>
          <p>
            This job is marked as cancelled and is no longer active. You can
            view the details, but no further actions can be taken.
          </p>
        </Alert>
      )}

      {/* Job Time Summary */}
      {doorTimeTracking.totalMinutes > 0 && (
        <Card className="time-summary-card mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <FaClock className="me-2" /> Job Time Summary
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="time-stat-large">
                  <div className="time-stat-value">
                    {formatDuration(doorTimeTracking.totalMinutes)}
                  </div>
                  <div className="time-stat-label">Total Time</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="time-stat-large">
                  <div className="time-stat-value">
                    {doorTimeTracking.totalHours.toFixed(1)}h
                  </div>
                  <div className="time-stat-label">Billable Hours</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="time-stat-large">
                  <div className="time-stat-value">
                    {doorTimeTracking.sessions.length}
                  </div>
                  <div className="time-stat-label">Work Sessions</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="time-stat-large">
                  <div className="time-stat-value">
                    <span
                      className={`status-badge ${doorTimeTracking.jobTimingStatus}`}
                    >
                      {doorTimeTracking.jobTimingStatus
                        .replace("_", " ")
                        .toUpperCase()}
                    </span>
                  </div>
                  <div className="time-stat-label">Status</div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Row className="job-info-row">
        <Col lg={6} className="mb-3 mb-lg-0">
          <Card className="info-card">
            <Card.Header>
              <h5 className="mb-0">
                <FaInfo className="me-2" /> Job Information
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="info-row">
                <div className="info-item">
                  <span className="info-label">
                    <FaUserAlt className="me-2" /> Customer
                  </span>
                  <span className="info-value">{job.customer_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FaMapMarkerAlt className="me-2" /> Address
                  </span>
                  <span className="info-value">{job.address || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FaUserAlt className="me-2" /> Contact
                  </span>
                  <span className="info-value">
                    {job.contact_name || "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FaPhoneAlt className="me-2" /> Phone
                  </span>
                  <span className="info-value">{job.phone || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FaGlobe className="me-2" /> Region
                  </span>
                  <span className="info-value">{job.region}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <FaTools className="me-2" /> Additional Notes
                  </span>
                  <span className="info-value">{job.job_scope || "N/A"}</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="info-card">
            <Card.Header>
              <h5 className="mb-0">
                <FaInfo className="me-2" /> Status Information
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="info-row">
                <div className="info-item">
                  <span className="info-label">Scheduled Date</span>
                  <span className="info-value">
                    <FaCalendarAlt className="me-2" />
                    {displayDate}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span
                    className={`status-text ${
                      isJobCancelled ? "text-danger" : ""
                    }`}
                  >
                    {job.status
                      ? job.status.charAt(0).toUpperCase() +
                        job.status.slice(1).replace(/_/g, " ")
                      : "No Status"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Material Ready</span>
                  <span className="status-text">
                    {job.material_ready ? "Yes" : "No"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Material Location</span>
                  <span className="info-value">
                    <FaBoxOpen className="me-2" />
                    {job.material_location === "S"
                      ? "Shop"
                      : job.material_location === "C"
                      ? "Client"
                      : job.material_location || "N/A"}
                  </span>
                </div>
              </div>

              {!isJobCancelled && (
                <div className="status-actions">
                  <div className="status-label">Update Status:</div>
                  <div className="status-buttons">
                    <Button
                      variant={
                        job.status === "unscheduled"
                          ? "secondary"
                          : "outline-secondary"
                      }
                      size="sm"
                      onClick={() => handleStatusChange("unscheduled")}
                      className="status-btn"
                    >
                      Unscheduled
                    </Button>
                    <Button
                      variant={
                        job.status === "scheduled"
                          ? "primary"
                          : "outline-primary"
                      }
                      size="sm"
                      onClick={() => handleStatusChange("scheduled")}
                      className="status-btn"
                    >
                      Scheduled
                    </Button>
                    <Button
                      variant={
                        job.status === "waiting_for_parts"
                          ? "warning"
                          : "outline-warning"
                      }
                      size="sm"
                      onClick={() => handleStatusChange("waiting_for_parts")}
                      className="status-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        opacity: 1,
                        backgroundColor: '#ffc107',
                        borderColor: '#ffc107',
                        color: '#212529',
                        backgroundImage: 'none'
                      }}
                    >
                      Waiting
                    </Button>
                    <Button
                      variant={
                        job.status === "on_hold" ? "danger" : "outline-danger"
                      }
                      size="sm"
                      onClick={() => handleStatusChange("on_hold")}
                      className="status-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        opacity: 1,
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545',
                        color: '#ffffff',
                        backgroundImage: 'none'
                      }}
                    >
                      On Hold
                    </Button>
                    <Button
                      variant={
                        job.status === "completed"
                          ? "success"
                          : "outline-success"
                      }
                      size="sm"
                      onClick={() => handleStatusChange("completed")}
                      className="status-btn"
                    >
                      Completed
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Doors Section with Time Tracking and Actions */}
      <Card className="doors-card mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaTools className="me-2" /> Door Details, Time Tracking & Actions
          </h5>
          <Badge bg="info">
            {job.doors ? job.doors.length : 0}{" "}
            {job.doors && job.doors.length === 1 ? "Door" : "Doors"}
          </Badge>
        </Card.Header>
        <Card.Body>
          {job.doors && job.doors.length === 0 ? (
            <Alert variant="info">No doors associated with this job.</Alert>
          ) : (
            <div className="doors-detailed-list">
              {job.doors &&
                job.doors.map((door, index) => {
                  const isExpanded = expandedDoors.has(door.id);
                  const doorNumber = door.door_number || index + 1;
                  const hasSpecs = renderDoorSpecs(door) !== null;
                  const estimatedMinutes = estimateDoorTime(door);

                  return (
                    <Card
                      key={door.id}
                      className={`door-card ${isExpanded ? "expanded" : ""}`}
                    >
                      <Card.Header
                        className="door-card-header"
                        onClick={() => toggleDoorExpansion(door.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="door-header-content">
                          <div className="door-header-left">
                            <h6 className="door-title mb-0">
                              <FaTools className="me-2" />
                              Door #{doorNumber}
                              {door.location && (
                                <span className="door-location">
                                  {" "}
                                  - {door.location}
                                </span>
                              )}
                            </h6>
                            <div className="door-summary">
                              {door.door_type && (
                                <Badge bg="secondary" className="me-2">
                                  {door.door_type}
                                </Badge>
                              )}
                              {door.width && door.height && (
                                <Badge bg="info" className="me-2">
                                  {door.width} × {door.height}{" "}
                                  {door.dimension_unit || "ft"}
                                </Badge>
                              )}
                              {estimatedMinutes > 0 && (
                                <Badge bg="warning" className="me-2">
                                  <FaClock className="me-1" />
                                  {formatDuration(estimatedMinutes)}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="door-header-right">
                            {door.completed ? (
                              <Badge bg="success">
                                <FaCheckCircle className="me-1" />
                                Completed
                              </Badge>
                            ) : (
                              <Badge bg="warning">In Progress</Badge>
                            )}
                            <FaExpand className="ms-3 expand-icon" />
                          </div>
                        </div>
                      </Card.Header>

                      {isExpanded && (
                        <Card.Body className="door-card-body">
                          <Row>
                            {/* Conditionally render Specifications Column */}
                            {hasSpecs && (
                              <Col lg={3} className="mb-3">
                                {renderDoorSpecs(door)}
                              </Col>
                            )}

                            {/* Time & Actions Column - larger if no specs */}
                            <Col lg={hasSpecs ? 3 : 4} className="mb-3">
                              {renderDoorTimeAndActions(door)}
                            </Col>

                            {/* Line Items Column - larger if no specs */}
                            <Col lg={hasSpecs ? 3 : 4} className="mb-3">
                              {renderDoorLineItems(door)}
                            </Col>

                            {/* Media Column - larger if no specs */}
                            <Col lg={hasSpecs ? 3 : 4} className="mb-3">
                              <div className="door-media-section">
                                <h6 className="media-title">
                                  <FaImage className="me-2" />
                                  Photos & Videos
                                </h6>
                                {renderDoorMedia(door)}
                              </div>
                            </Col>
                          </Row>

                          {/* Status Summary */}
                          <div className="door-status-summary">
                            {door.completed ? (
                              <div className="status-completed">
                                <FaCheckCircle className="me-2 text-success" />
                                <strong>Door Completed & Signed Off</strong>
                                <small className="text-muted ms-2">
                                  {door.completed_at
                                    ? new Date(
                                        door.completed_at
                                      ).toLocaleDateString()
                                    : "Completion date not available"}
                                </small>
                              </div>
                            ) : (
                              <div className="status-pending">
                                <FaExclamationTriangle className="me-2 text-warning" />
                                <strong>Door In Progress</strong>
                                <small className="text-muted ms-2">
                                  Complete work items and capture media/signature to finish
                                </small>
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      )}
                    </Card>
                  );
                })}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Purchase Orders Section */}
      <Card className="purchase-orders-card mt-4">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setPurchaseOrdersExpanded(!purchaseOrdersExpanded)}
        >
          <h5 className="mb-0">
            <FaFileInvoiceDollar className="me-2" /> Purchase Orders
          </h5>
          <div className="d-flex align-items-center">
            <Badge bg="info" className="me-3">
              {purchaseOrders.length}{" "}
              {purchaseOrders.length === 1 ? "Order" : "Orders"}
            </Badge>
            {purchaseOrdersExpanded ? (
              <FaChevronDown className="expand-icon" />
            ) : (
              <FaChevronRight className="expand-icon" />
            )}
          </div>
        </Card.Header>
        {purchaseOrdersExpanded && (
          <Card.Body>
            {loadingPurchaseOrders ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading purchase orders...
              </div>
            ) : purchaseOrders.length === 0 ? (
              <div className="text-center py-4">
                <FaFileInvoiceDollar
                  className="text-muted mb-2"
                  style={{ fontSize: "2rem" }}
                />
                <p className="text-muted mb-3">
                  No purchase orders for this job yet.
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/purchase-orders/new?job_id=${jobId}`)}
                >
                  <FaFileInvoiceDollar className="me-2" />
                  Create Purchase Order
                </Button>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover purchase-orders-table">
                    <thead>
                      <tr>
                        <th>PO Number</th>
                        <th>Vendor</th>
                        <th>Date</th>
                        <th>Shipped Via</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrders.map((po) => (
                        <tr key={po.id}>
                          <td>
                            <strong>{po.po_number}</strong>
                          </td>
                          <td>{po.to_field || "N/A"}</td>
                          <td>
                            {po.date
                              ? new Date(po.date).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td>{po.how_shipped || "N/A"}</td>
                          <td className="text-end">
                            ${(po.total_price || 0).toFixed(2)}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => navigate(`/purchase-orders/${po.id}`)}
                              title="View Details"
                            >
                              <FaExternalLinkAlt />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await api.get(
                                    `/purchase-orders/${po.id}/pdf`,
                                    { responseType: "blob" }
                                  );
                                  const url = window.URL.createObjectURL(
                                    new Blob([response.data])
                                  );
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.download = `PO_${po.po_number}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  toast.error("Failed to download PDF");
                                }
                              }}
                              title="Download PDF"
                            >
                              <FaArrowDown />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/purchase-orders/new?job_id=${jobId}`)}
                  >
                    <FaFileInvoiceDollar className="me-2" />
                    Create New Purchase Order
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        )}
      </Card>

      {/* Cancel Job Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        className="cancel-job-modal"
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FaExclamationTriangle className="me-2" />
            Confirm Job Cancellation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <FaExclamationTriangle
              className="text-danger"
              style={{ fontSize: "3rem" }}
            />
          </div>

          <p className="fw-bold text-center">
            Are you sure you want to cancel Job #{formatJobNumber(job.job_number)}?
          </p>

          <Alert variant="warning">
            <p className="mb-0">
              <strong>Warning:</strong> This action will mark the job as
              cancelled. Cancelled jobs cannot be scheduled or worked on. This
              action cannot be easily undone.
            </p>
          </Alert>

          <p>
            Customer: <strong>{job.customer_name}</strong>
            <br />
            Address: <strong>{job.address || "N/A"}</strong>
            <br />
            Scheduled Date: <strong>{displayDate}</strong>
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Reason for cancellation (optional):</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter reason for cancellation..."
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
            />
            <Form.Text className="text-muted">
              This will be recorded in the job history for reference.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={isCancelling}
          >
            No, Keep Job Active
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelJob}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Cancelling...
              </>
            ) : (
              <>Yes, Cancel Job</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Items to Job Modal (for special jobs) */}
      <AddItemsToJobModal
        show={showAddItemsModal}
        onHide={() => setShowAddItemsModal(false)}
        job={job}
        onItemsAdded={() => {
          // Reload job data to show new items
          loadJob();
        }}
      />
    </div>
  );
};

export default JobDetails;